import {
  Button,
  ModalityCodesEnum,
  SelectOptionType,
  addToast,
} from '@octano/global-ui';
import { useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import DisplayError from '../../../../components/info/DisplayError';
import { SectionTitle } from '../../../../components/text';
import { useSearchNonStructuredAssistant } from '../../../../hooks/remoteSearchSelect/useSearchNonStructuredAssistant';
import { useSearchNonStructuredTeachers } from '../../../../hooks/remoteSearchSelect/useSearchNonStructuredTeachers';
import { useValidations } from '../../../../hooks/useValidations';
import { FormDataResponseType } from '../../../../types/sectionMaintainerTypes';
import { NonStructuredSectionValuesForm } from '../../types/NonStructuredSectionForm.types';
import {
  TeachersFields,
  AssistantsFields,
} from './NonStructuredSectionFormFields';
import { NonStructuredSectionGeneralFields } from './NonStructuredSectionGeneralFields';
import { useNonStructuredSectionForm } from './useNonStructuredSectionForm';
import { useNonStructuredSectionsFiltersFormLoaderData } from '../hooks/NonStructuredSectionsFiltersFormLoader';
import { createNonStructuredSectionRequest } from '../../api';

interface Props {
  onCancel: () => void;
  onSuccess: (id: number) => void;
  formData: FormDataResponseType | undefined;
  loading: boolean;
  error?: any;
  retry: () => void;
}

export function NonStructuredSectionForm(props: Props): JSX.Element {
  const {
    onCancel,
    onSuccess,
    formData,
    loading: loadingFormData,
    error: errorFormData,
    retry,
  } = props;
  const { t } = useTranslation();
  const prefix = 'sectionsMaintainer.create';

  const { msgValidations, rulesPatternNumeric } = useValidations();
  const searchTeachers = useSearchNonStructuredTeachers();
  const searchAssistants = useSearchNonStructuredAssistant();
  const methods = useForm<NonStructuredSectionValuesForm>({
    defaultValues: {
      modalityName: '',
      teachers: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
      assistants: [
        {
          value: 0,
          label: '',
          isOnline: false,
          isPresential: false,
          order: 0,
        },
      ],
    },
  });
  const { control, setValue } = methods;
  const scopedForm = useNonStructuredSectionsFiltersFormLoaderData();
  const {
    coursesOptions,
    courseNamesOptions,
    courses,
    loadingCourses,
    facultyOptions,
    campusOptions,
    schedulesOptions,
    loadCourses,
  } = useNonStructuredSectionForm(formData, scopedForm);
  const [selectedFaculty, selectedCourse] = useWatch({
    control,
    name: ['faculty', 'course'],
  });
  const selectedFacultyId = selectedFaculty?.value
    ? Number(selectedFaculty.value)
    : undefined;

  useEffect(() => {
    if (selectedFacultyId) {
      loadCourses(selectedFacultyId);
      setValue('course', null as any);
      setValue('courseName', null as any);
      setValue('modalityName', '');
    } else {
      loadCourses();
    }
  }, [selectedFacultyId, loadCourses, setValue]);

  const currentCourse = useMemo(
    () => courses.find((c) => c.code === selectedCourse?.value),
    [courses, selectedCourse],
  );

  const currentDeliveryMode = useMemo(
    () =>
      currentCourse
        ? (currentCourse as any)?.modality?.deliveryMode || ModalityCodesEnum.PR
        : ModalityCodesEnum.PR,
    [currentCourse],
  );

  const supportsPresential = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.PR,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DU,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const supportsOnline = useMemo(
    () =>
      !!currentDeliveryMode &&
      [
        ModalityCodesEnum.OL,
        ModalityCodesEnum.SP,
        ModalityCodesEnum.DI,
        ModalityCodesEnum.OA,
      ].includes(currentDeliveryMode),
    [currentDeliveryMode],
  );

  const onCourseChange = (option: SelectOptionType) => {
    setValue('course', option);
    if (option?.value) {
      const courseName = courseNamesOptions.find(
        (c) => (c as any).code === option.value,
      );
      if (courseName) {
        setValue('courseName', courseName);
      }
    } else {
      setValue('courseName', null as any);
    }
  };

  const onCourseNameChange = (option: SelectOptionType) => {
    setValue('courseName', option);
    if (option?.value) {
      const course = coursesOptions.find(
        (c) => c.value === (option as any).code,
      );
      if (course) {
        setValue('course', course);
      }
    } else {
      setValue('course', null as any);
    }
  };

  const onSubmit = async (values: NonStructuredSectionValuesForm) => {
    const activePeriod = formData?.periods?.find((p) => p.active);

    if (!activePeriod || !values.course?.value) {
      addToast({ text: t('common.error'), color: 'danger' });
      return;
    }

    const professors = values.teachers.filter((item) => !!item.value);

    if (professors.length === 0) {
      addToast({
        text: t(`${prefix}.atLeastOneTeacherError`),
        color: 'danger',
      });
      return;
    }

    const { data, error } = await createNonStructuredSectionRequest({
      maximumStudent: Number(values.quota),
      campusId: Number(values.campus.value),
      courseCode: values.course.value.toString(),
      periodId: activePeriod.id,
      name: `${values.sectionCode}-${values.name}`,
      scheduleId: values.schedule?.value
        ? Number(values.schedule.value)
        : undefined,
      professors,
      assistants: values.assistants.filter((item) => !!item.value),
    });

    if (data && !error) {
      addToast({ text: t('common.saveSuccess'), color: 'success' });
      onSuccess(data.id);
    } else {
      const errorMessage = (error as any)?.data?.message;
      if (errorMessage === 'SECTION_NAME_ALREADY_EXISTS') {
        addToast({
          text: t(`${prefix}.sectionNameAlreadyExistsError`),
          color: 'danger',
        });
      } else {
        addToast({
          text: t('common.saveError'),
          color: 'danger',
        });
      }
    }
  };

  if (errorFormData) {
    return (
      <DisplayError textBody={errorFormData.toString()} retryAction={retry} />
    );
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Row className="pb-4">
          <NonStructuredSectionGeneralFields
            control={control}
            facultyOptions={facultyOptions}
            currentModalityName={
              currentCourse
                ? currentCourse?.modality?.name?.trim() || '-'
                : undefined
            }
            coursesOptions={coursesOptions}
            courseNamesOptions={courseNamesOptions}
            campusOptions={campusOptions}
            schedulesOptions={schedulesOptions}
            msgValidations={msgValidations}
            rulesPatternNumeric={rulesPatternNumeric}
            loadingFormData={loadingFormData}
            loadingCourses={loadingCourses}
            t={t}
            prefix={prefix}
            onCourseChange={onCourseChange}
            onCourseNameChange={onCourseNameChange}
          />
        </Row>

        <Row className="pb-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.teacherTitle`)} />
            <p className="fs-16">{t(`${prefix}.teacherDescription`)}</p>
          </Col>
          <TeachersFields
            t={t}
            prefix={prefix}
            searchTeachers={searchTeachers}
            supportsOnline={supportsOnline}
            supportsPresential={supportsPresential}
          />
          <AssistantsFields
            t={t}
            prefix={prefix}
            searchAssistants={searchAssistants}
            supportsOnline={supportsOnline}
            supportsPresential={supportsPresential}
          />
        </Row>

        <Row className="py-5 justify-content-end pt-5">
          <Col className="" xs={12} lg={3}>
            <Button
              outlined
              color="primary"
              onClick={onCancel}
              className="mr-2"
              fullwidth
              text={t('common.terms.cancel')}
            />
          </Col>
          <Col xs={12} lg={3} className="pb-2 order-2 order-lg-1">
            <Button
              color="primary"
              type="submit"
              fullwidth
              text="+ Crear y continuar"
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}
